const _excluded = ["identifier"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], -1 === t.indexOf(o) && {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (-1 !== e.indexOf(n)) continue; t[n] = r[n]; } return t; }
import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { ButtonGroup } from 'reactstrap';
import { kebabCase } from 'lodash';
import classnames from 'classnames';
import moment from 'moment';
import { conformanceSelector, enabledSelector, labelSelector, newPathSelector, optionsSelector, valueSelector } from '../selectors/index';
import ConformanceTooltip from './conformanceTooltip';
import entityComposition from '../../common/classes/entityComposition';
import { UncomposedSegmentedButton } from './segmentedButton';
import Label from './label';
import logValueOnChange from '../commonActions/logValueOnChange';
import localChangeSelector from '../selectors/localChangeSelector';
const OptionPickerButton = ({
  allowsClear: _allowsClear = true,
  className: _className = '',
  conformanceText: _conformanceText = null,
  enabled,
  label: _label = '',
  localChangeFunc,
  onChange,
  options: _options = [],
  path,
  value: _value = null,
  testingIdentifier: _testingIdentifier = ''
}) => {
  const showConformance = !_conformanceText;
  const handleChange = useCallback(async incomingValue => {
    const newIncomingValue = incomingValue && _value && !_value[incomingValue.identifier];
    let clearValue = false;
    const mergePatch = {};
    if (_value && newIncomingValue) {
      mergePatch[Object.keys(_value)[0]] = null;
    }
    // if select button is changing to another selection...
    if (incomingValue && (!_value || newIncomingValue)) {
      const {
          identifier: storageKey
        } = incomingValue,
        rest = _objectWithoutProperties(incomingValue, _excluded);
      // const storageKey = incomingValue.identifier;
      mergePatch[storageKey] = _objectSpread(_objectSpread({}, rest), {}, {
        title: incomingValue.title,
        // eslint-disable-next-line no-underscore-dangle
        _created: typeof incomingValue._created !== 'undefined' ? incomingValue._created : moment().toISOString()
      });
    } else if (_allowsClear) {
      // if the button is being cleared...
      clearValue = true;
    }
    const keys = Object.keys(mergePatch);
    if (clearValue) {
      await onChange(localChangeFunc({
        path,
        value: null
      }));
    } else if (keys.length > 0) {
      let title;
      keys.forEach(identifier => {
        if (mergePatch[identifier] !== null) {
          ({
            title
          } = mergePatch[identifier]);
        }
      });
      await onChange(localChangeFunc({
        path,
        value: mergePatch,
        displayValue: title
      }));
    } // else: no change - do nothing.
  }, [_value, _allowsClear, onChange, localChangeFunc, path]);
  const preparedOptions = useMemo(() => {
    const rawOptions = JSON.parse(JSON.stringify(_options));
    const newOptions = [];
    rawOptions.forEach((opt, index) => {
      const newOption = JSON.parse(JSON.stringify(opt));
      /* eslint-disable no-underscore-dangle */
      if (typeof newOption._created === 'undefined') {
        newOption._created = index + 1;
      }
      /* eslint-enable no-underscore-dangle */
      newOptions.push(newOption);
    });
    return newOptions;
  }, [_options]);
  const preparedValue = useMemo(() => {
    let result = {};
    if (typeof _value === 'object' && _value !== null && _value[Object.keys(_value)[0]] !== null) {
      const key = Object.keys(_value)[0];
      result = {
        id: key,
        value: (_value[key] || {}).title,
        identifier: key
      };
    }
    return result;
  }, [_value]);
  const activeValue = preparedValue && typeof preparedValue === 'object' ? preparedValue.identifier : preparedValue;
  const testingIdentifierClass = _testingIdentifier ? `t-opb-${kebabCase(_testingIdentifier)}` : '';
  const classNames = classnames('tqc-control', 's-opb', 't-opb', _className, testingIdentifierClass, {
    [`t-opb-${kebabCase(_label)}`]: _label !== null && _label.length
  }, showConformance ? null : 'is-invalid');
  const idKey = _testingIdentifier || _label;
  const id = `t-opb-${kebabCase(idKey)}`;
  return /*#__PURE__*/React.createElement("div", {
    id: id,
    className: classNames
  }, /*#__PURE__*/React.createElement(Label, {
    value: _label
  }), /*#__PURE__*/React.createElement(ButtonGroup, {
    style: {
      display: 'flex'
    }
  }, preparedOptions.map(option => /*#__PURE__*/React.createElement(UncomposedSegmentedButton, {
    key: `SGBTN_${option.identifier}`,
    active: activeValue === option.identifier,
    enabled: enabled,
    onChange: handleChange,
    option: option,
    label: _label
  }))), /*#__PURE__*/React.createElement(ConformanceTooltip, {
    errorText: _conformanceText,
    target: id,
    placement: "right"
  }));
};
OptionPickerButton.propTypes = {
  allowsClear: PropTypes.bool,
  localChangeFunc: PropTypes.func.isRequired,
  className: PropTypes.string,
  enabled: PropTypes.bool.isRequired,
  label: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({})),
  path: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.string]),
  conformanceText: PropTypes.string,
  testingIdentifier: PropTypes.string
};
export { OptionPickerButton as UncomposedOptionPickerButton };
export default entityComposition(OptionPickerButton, [], {
  conformanceText: conformanceSelector,
  enabled: enabledSelector,
  label: labelSelector,
  options: optionsSelector,
  path: newPathSelector,
  value: valueSelector,
  onChange: logValueOnChange,
  localChangeFunc: localChangeSelector
});